/**
 * RowCellData — per-row cell store backed by `SortedDeque` for
 * O(log n + k) range queries and O(1) boundary pushes.
 *
 * One instance per source row. The sheet-level `SourceRowCellData`
 * registry owns a `Map<RowLocalId, RowCellData>` and routes reads /
 * writes to the appropriate instance.
 *
 * Storage layout — mirrors `tento/tento-lona-js/src/sync/row-data.ts`:
 *
 *   - `single` cardinality rows use the sorted date index
 *     (`SortedDeque`), keyed by `date.start.dse` (days since epoch)
 *     with a secondary Map<`${type}:${index}`> for O(1) point lookups.
 *   - `multi` cardinality rows key cells by id (many cells can share a
 *     date: events, scheduling slots) and keep them ordered by
 *     (first day, insertion order) (`MultiIndex`), so a ranged read is
 *     O(log n + k) however many cells the row holds.
 *
 * Each lane also keeps its extent — the first day any of its cells
 * starts and the day after the last one ends (`dateExtent`,
 * `timepointExtent`) — so a whole-sheet span (the formula engine's
 * `observedRange`) costs O(rows), not a copy of every cell.
 *
 * RowCellData is pure storage. Invalidation emission is the caller's
 * responsibility (`SourceRowCellData` emits `ColumnDataChanged`).
 */

import type { Option } from "../core/option.ts";
import { SortedDeque } from "@tento-core/sorted-deque";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";

import { type CellCardinality, CellEntry, type CellStyle, type CellValue } from "../core/dtype.ts";
import { CellId } from "../ids/ids.ts";
import { frozenCopy } from "../core/frozen-copy.ts";

/**
 * Work counters of every `RowCellData` in the process, for the bench harness
 * and the complexity tests (nothing in the SDK reads them): the cells a
 * multi-cardinality ranged read visited (`multiCellsVisited`, merges
 * included), the cells an extent pass visited (`extentCellsVisited`) and the
 * timepoint work (`timepointCellsVisited`): the cells a ranged timepoint read
 * answered plus every binary-search probe of the timepoint order (reads and
 * writes), so a search that degraded to a scan would show.
 */
export const RowCellDataWork = { multiCellsVisited: 0, extentCellsVisited: 0, timepointCellsVisited: 0 };

/** Unique secondary key per cell; survives DSE collisions across timescales. */
function dateKey(date: NaiveDate.Partial): string {
  return `${date.type}:${date.index}`;
}

export class RowCellData {
  readonly #sortedIndex = new SortedDeque<CellEntry, string>((e) =>
    dateKey(e.date)
  );
  readonly #multi = new MultiIndex();
  /**
   * Datetime-positioned "custom" cells, keyed by raw cell id. Mirrors
   * the lona-js `RowData._customStore` (`src/sync/row-data.ts`): a cell
   * whose wire date carries a time component (sticker overlays,
   * scheduling slots) lives here as a `CellEntry.Timepoint` with a
   * `DateTime.Range` (`dtr`), independent of the row's single/multi
   * cardinality. Date-granular reads (`cells`) never see these; custom
   * reads (`customCells`) never see the date-keyed lanes.
   */
  readonly #timepointsById = new Map<string, CellEntry.Timepoint>();
  /**
   * The timepoint cells in read order — by start, then by when their id was
   * first written (the order a stable sort of `#timepointsById` gives) —
   * kept on write, so a ranged read is two binary searches and a slice.
   */
  readonly #timepointOrder: CellEntry.Timepoint[] = [];
  /** Per timepoint id, its first-write sequence (the tie order). */
  readonly #timepointSeq = new Map<string, number>();
  #nextTimepointSeq = 0;
  /** The date-keyed lanes' extent (`dateExtent`), kept on write. */
  readonly #dateSpan = new Span();
  /** The timepoint lane's extent, recomputed after a change (`timepointExtent`). */
  #timepointSpan: Option<{ readonly horizonDse: number; readonly extent: Option<RowCellData.Extent> }> = null;

  /**
   * The copies this row's cells hold that `frozenCopy` made fresh from a
   * written value (plain top-level objects only): the store's proof that a
   * value it hands out is its own decoded copy, not a caller's object
   * (`CellDataStore.ownsCellValue`). Shared with the owning store's ledger
   * (`useCopyLedger`); a free-standing row keeps its own.
   */
  #copies = new WeakSet<object>();
  /** Bumped by every write that may change what a read answers (`revision`). */
  #revision = 0;

  constructor(readonly cardinality: CellCardinality = "single") {}

  /**
   * A change counter: it moves on every `set`, `init`, `clear`, `setCustom`,
   * `removeCustom`, `clearCustomInRange` and `clearAll` (a write that changes
   * nothing may move it too). Equal revisions of one `RowCellData` mean the
   * same cells, so a reader can memoize on (this, revision) in O(1).
   */
  get revision(): number {
    return this.#revision;
  }

  /** Record this row's fresh copies in `ledger` (its store's), from now on. */
  useCopyLedger(ledger: WeakSet<object>): void {
    this.#copies = ledger;
  }

  /** Whether `value` is a copy this row made of a written value (`#copies`). */
  ownsCopy(value: object): boolean {
    return this.#copies.has(value);
  }

  /** `frozenCopy(value)`, remembering a fresh plain-object copy as this row's own. */
  #heldCopy(value: CellValue): CellValue {
    const copy = frozenCopy(value);
    if (
      copy !== value && typeof copy === "object" && copy !== null && !Array.isArray(copy) &&
      Object.getPrototypeOf(copy) === Object.prototype
    ) {
      this.#copies.add(copy);
    }
    return copy;
  }

  get size(): number {
    const base = this.cardinality === "multi"
      ? this.#multi.size
      : this.#sortedIndex.size;
    return base + this.#timepointsById.size;
  }

  /**
   * Read one cell. `cellId` is required for `multi` rows and
   * forbidden for `single` rows; mirrors the server contract
   * `Cell::ranged(_, _, _, Some(id))` for multi-card storage.
   */
  get(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellValue> {
    const entry = this.peekEntry(date, cellId);
    return entry == null ? null : entry.value;
  }

  /**
   * The stored (frozen, shared) entry at `(date, cellId)`, or `null`:
   * on a `multi` row the cell with id `cellId` (wherever it is dated —
   * compare `date` yourself), on a `single` row the cell of `date`.
   */
  peekEntry(
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): Option<CellEntry> {
    if (this.cardinality === "multi") {
      this.#requireCellId(cellId, "get", date);
      return this.#multi.get(cellId);
    }
    return this.#sortedIndex.get(dateKey(date)) ?? null;
  }

  /**
   * Write one cell. Upserts in place when a cell at the same
   * `(date, cellId)` already exists. `style` is the optional
   * per-cell style bag populated by formula `cell.bg` calls.
   * The entry kept is frozen, its value a deeply frozen copy
   * (`frozenCopy`), so every read shares one entry no read can write.
   */
  set(
    date: NaiveDate.Partial,
    value: CellValue,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
  ): void {
    this.#revision++;
    if (this.cardinality === "multi") {
      this.#requireCellId(cellId, "set", date);
      const entry: CellEntry = Object.freeze({
        date,
        id: cellId,
        value: this.#heldCopy(value),
        style: style ?? null,
      });
      const replaced = this.#multi.set(cellId, entry);
      if (replaced != null && !sameDays(replaced.date, date)) this.#dateSpan.remove(replaced.date);
      this.#dateSpan.add(date);
      return;
    }
    const entry: CellEntry = Object.freeze({
      date,
      id: null,
      value: this.#heldCopy(value),
      style: style ?? null,
    });
    // Same key, same date: an upsert never moves the extent.
    this.#sortedIndex.upsert(date.start.dse, entry);
    this.#dateSpan.add(date);
  }

  /**
   * Seed one cell without any side effects. Alias of `set` — exists
   * so callers can signal "this is a hydrate-time seed, not a user
   * edit" at the type layer (useful when SourceRowCellData emits on
   * `set` but not on `init`).
   */
  init(
    date: NaiveDate.Partial,
    value: CellValue,
    cellId: Option<CellId>,
    style: Option<CellStyle>,
  ): void {
    this.set(date, value, cellId, style);
  }

  /** Delete one cell. No-op if absent. */
  clear(date: NaiveDate.Partial, cellId: Option<CellId>): void {
    this.#revision++;
    if (this.cardinality === "multi") {
      this.#requireCellId(cellId, "clear", date);
      const removed = this.#multi.delete(cellId);
      if (removed != null) this.#dateSpan.remove(removed.date);
      return;
    }
    const key = dateKey(date);
    const removed = this.#sortedIndex.get(key);
    if (this.#sortedIndex.deleteByKey(key) && removed != null) this.#dateSpan.remove(removed.date);
  }

  #requireCellId(
    cellId: Option<CellId>,
    op: "get" | "set" | "clear",
    date: NaiveDate.Partial,
  ): asserts cellId is CellId {
    if (cellId == null) {
      throw new Error(
        `RowCellData.${op}: multi-cardinality row requires cellId (date="${date.toString()}").`,
      );
    }
  }

  /**
   * Read all cells, optionally filtered to `range` (by first day,
   * start inclusive, end exclusive), ordered by first day.
   *
   * `single` rows: O(log n + k) via the sorted index.
   * `multi` rows:  O(log n + k) via the ordered id index; cells that
   *                start on the same day keep the order their ids were
   *                first written in.
   */
  cells(range: Option<NaiveDate.Range>): readonly CellEntry[] {
    if (this.cardinality === "multi") return this.#multi.inRange(range);
    if (range) {
      return this.#sortedIndex.getInRange(range.start.dse, range.end.dse);
    }
    const out: CellEntry[] = [];
    this.#sortedIndex.forEach((_, entry) => out.push(entry));
    return out;
  }

  /**
   * The date-keyed cells' extent: the first day any starts on and the day
   * after the last one ends (`date.end`), or `null` with none. O(1) after a
   * write; a removal of the first or the last cell costs one pass over the
   * row the next time it is read. No cell is copied.
   */
  dateExtent(): Option<RowCellData.Extent> {
    return this.#dateSpan.extent(() => {
      const span = new Span();
      if (this.cardinality === "multi") this.#multi.forEachEntry((entry) => span.add(entry.date));
      else this.#sortedIndex.forEach((_, entry) => span.add(entry.date));
      RowCellDataWork.extentCellsVisited += this.cardinality === "multi" ? this.#multi.size : this.#sortedIndex.size;
      return span;
    });
  }

  /**
   * The timepoint cells' extent in days: the first day any starts on and
   * the largest end day, where a cell ending at or after `horizonDse` (an
   * unbounded recurring master stretched to the fetch sentinel) counts its
   * start day instead. `null` with none. Recomputed once after a change.
   */
  timepointExtent(horizonDse: number): Option<RowCellData.Extent> {
    const cached = this.#timepointSpan;
    if (cached != null && cached.horizonDse === horizonDse) return cached.extent;
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const tp of this.#timepointsById.values()) {
      const startDse = tp.dtr.start.date.dse;
      const endDse = tp.dtr.end.date.dse;
      min = Math.min(min, startDse);
      max = Math.max(max, endDse >= horizonDse ? startDse : endDse);
    }
    RowCellDataWork.extentCellsVisited += this.#timepointsById.size;
    const extent = Number.isFinite(min) ? Object.freeze({ minDse: min, maxDse: max }) : null;
    this.#timepointSpan = { horizonDse, extent };
    return extent;
  }

  // ── Custom (datetime-positioned) cells ──────────────────────────
  //
  // Parallel to the date-keyed lanes; mirrors `RowData`'s custom store
  // semantics so the canonical substrate can hold sticker / scheduling
  // cells without a per-row-handle store. All methods are no-ops for
  // rows that never carry datetime cells.

  /** Upsert one datetime cell by its raw id; kept frozen, its value a deeply frozen copy. */
  setCustom(entry: CellEntry.Timepoint): void {
    this.#revision++;
    const held = Object.freeze({ ...entry, value: this.#heldCopy(entry.value) });
    const prior = this.#timepointsById.get(entry.id);
    if (prior != null) this.#unorder(prior);
    else this.#timepointSeq.set(entry.id, this.#nextTimepointSeq++);
    this.#timepointsById.set(entry.id, held);
    this.#timepointOrder.splice(this.#orderIndex(held.dtr.start.mse, this.#seqOf(held)), 0, held);
    this.#timepointSpan = null;
  }

  /** Read one datetime cell by raw id. `null` if unset. */
  getCustom(id: string): Option<CellEntry.Timepoint> {
    return this.#timepointsById.get(id) ?? null;
  }

  /**
   * Remove ONE datetime cell by raw id. Returns its range, or `null`
   * when absent. Single-cell counterpart of `clearCustomInRange`.
   */
  removeCustom(id: string): Option<DateTime.Range<Utc>> {
    const entry = this.#timepointsById.get(id);
    if (!entry) return null;
    this.#revision++;
    this.#unorder(entry);
    this.#timepointsById.delete(id);
    this.#timepointSeq.delete(id);
    this.#timepointSpan = null;
    return entry.dtr;
  }

  /** Remove datetime cells whose start falls within `range` (half-open). */
  clearCustomInRange(range: DateTime.Range<Utc>): void {
    const from = this.#orderIndex(range.start.mse, -1);
    const to = this.#orderIndex(range.end.mse, -1);
    if (to <= from) return;
    this.#revision++;
    for (const entry of this.#timepointOrder.splice(from, to - from)) {
      this.#timepointsById.delete(entry.id);
      this.#timepointSeq.delete(entry.id);
    }
    this.#timepointSpan = null;
  }

  /**
   * Read datetime cells, optionally filtered to `range` (start
   * inclusive, end exclusive), sorted by start (ties: the order their ids
   * were first written). O(log n + k): the read order is kept on write.
   */
  customCells(
    range: Option<DateTime.Range<Utc>>,
  ): readonly CellEntry.Timepoint[] {
    if (range == null) return this.#timepointOrder.slice();
    const out = this.#timepointOrder.slice(
      this.#orderIndex(range.start.mse, -1),
      this.#orderIndex(range.end.mse, -1),
    );
    // Work, not results: the cells answered plus the search probes above.
    RowCellDataWork.timepointCellsVisited += out.length;
    return out;
  }

  /** The first position in `#timepointOrder` at or after (`startMse`, `seq`). */
  #orderIndex(startMse: number, seq: number): number {
    const order = this.#timepointOrder;
    let lo = 0;
    let hi = order.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      RowCellDataWork.timepointCellsVisited++;
      const at = order[mid];
      if (at != null && (at.dtr.start.mse < startMse || (at.dtr.start.mse === startMse && this.#seqOf(at) < seq))) {
        lo = mid + 1;
      } else hi = mid;
    }
    return lo;
  }

  #seqOf(entry: CellEntry.Timepoint): number {
    return this.#timepointSeq.get(entry.id) ?? 0;
  }

  /** Take `entry` (a held cell) out of the read order. */
  #unorder(entry: CellEntry.Timepoint): void {
    const at = this.#orderIndex(entry.dtr.start.mse, this.#seqOf(entry));
    if (this.#timepointOrder[at] === entry) this.#timepointOrder.splice(at, 1);
  }

  /** Drop every cell. */
  clearAll(): void {
    this.#revision++;
    this.#sortedIndex.clear();
    this.#multi.clear();
    this.#timepointsById.clear();
    this.#timepointOrder.length = 0;
    this.#timepointSeq.clear();
    this.#dateSpan.reset();
    this.#timepointSpan = null;
  }
}

/** Types that go with `RowCellData`: `Extent`. */
export namespace RowCellData {
  /** A lane's day span: `minDse` the first day a cell starts, `maxDse` the day its last cell ends. */
  export interface Extent {
    readonly minDse: number;
    readonly maxDse: number;
  }
}

/**
 * A lane's `[first start, last end)` in days, kept as cells are written. A
 * write widens it in O(1); removing a cell that sat on either edge marks it
 * stale, and the next read recomputes it with one pass (`extent`).
 */
class Span {
  #min = Number.POSITIVE_INFINITY;
  #max = Number.NEGATIVE_INFINITY;
  #stale = false;
  #frozen: Option<RowCellData.Extent> = null;

  add(date: NaiveDate.Partial): void {
    if (this.#stale) return;
    const start = date.start.dse;
    const end = date.end.dse;
    if (start < this.#min) {
      this.#min = start;
      this.#frozen = null;
    }
    if (end > this.#max) {
      this.#max = end;
      this.#frozen = null;
    }
  }

  remove(date: NaiveDate.Partial): void {
    if (this.#stale) return;
    if (date.start.dse <= this.#min || date.end.dse >= this.#max) {
      this.#stale = true;
      this.#frozen = null;
    }
  }

  reset(): void {
    this.#min = Number.POSITIVE_INFINITY;
    this.#max = Number.NEGATIVE_INFINITY;
    this.#stale = false;
    this.#frozen = null;
  }

  /** The extent, recomputed through `rebuild` when a removal left it stale. */
  extent(rebuild: () => Span): Option<RowCellData.Extent> {
    if (this.#stale) {
      const fresh = rebuild();
      this.#min = fresh.#min;
      this.#max = fresh.#max;
      this.#stale = false;
      this.#frozen = null;
    }
    if (!Number.isFinite(this.#min)) return null;
    this.#frozen ??= Object.freeze({ minDse: this.#min, maxDse: this.#max });
    return this.#frozen;
  }
}

/** One multi-cardinality cell: its entry, and the order its id was first written in. */
type MultiSlot = { readonly entry: CellEntry; readonly seq: number };

/**
 * Sequence numbers stay below this, so `dse * SEQ_SPAN + seq` is an exact
 * integer in a double for every day through 9999 (|dse| < 2.95e6) and orders
 * by (day, seq).
 */
const SEQ_SPAN = 2 ** 31;

/**
 * A multi-cardinality row's cells by id, ordered by (first day, the order
 * each id was first written in) — the order a scan of the id map sorted
 * stably by first day returns, which is what reads have always seen: an id
 * written again keeps its place, an id cleared and written again goes last.
 *
 * The order is a `SortedDeque` keyed by the integer `dse * SEQ_SPAN + seq`
 * (unique per cell; renumbered after 2³¹ first writes), so a ranged
 * read is a binary search plus the cells it returns, and cells arriving at
 * either end of the row's history (a scroll's fetch) are O(1) pushes.
 */
class MultiIndex {
  readonly #byId = new Map<CellId, MultiSlot>();
  readonly #order = new SortedDeque<CellEntry, string>((e) => e.id ?? "");
  #seq = 0;

  get size(): number {
    return this.#byId.size;
  }

  get(id: CellId): Option<CellEntry> {
    return this.#byId.get(id)?.entry ?? null;
  }

  /** Upsert; returns the entry replaced, if any. */
  set(id: CellId, entry: CellEntry): Option<CellEntry> {
    const prev = this.#byId.get(id);
    if (prev == null) {
      if (this.#seq + 1 >= SEQ_SPAN) this.#renumber();
      const slot: MultiSlot = { entry, seq: ++this.#seq };
      this.#byId.set(id, slot);
      this.#order.upsert(orderKey(slot), entry);
      return null;
    }
    const slot: MultiSlot = { entry, seq: prev.seq };
    this.#byId.set(id, slot);
    if (prev.entry.date.start.dse === entry.date.start.dse) {
      // Same place: replaced in the deque where it stands.
      this.#order.upsert(orderKey(slot), entry);
    } else {
      this.#order.deleteByKey(id);
      this.#order.upsert(orderKey(slot), entry);
    }
    return prev.entry;
  }

  /** Remove; returns the entry removed, if any. */
  delete(id: CellId): Option<CellEntry> {
    const prev = this.#byId.get(id);
    if (prev == null) return null;
    this.#byId.delete(id);
    this.#order.deleteByKey(id);
    return prev.entry;
  }

  clear(): void {
    this.#byId.clear();
    this.#order.clear();
  }

  forEachEntry(fn: (entry: CellEntry) => void): void {
    for (const slot of this.#byId.values()) fn(slot.entry);
  }

  /** Cells whose first day is in `range` (all with `null`), in (first day, first-written) order. */
  inRange(range: Option<NaiveDate.Range>): CellEntry[] {
    let out: CellEntry[];
    if (range == null) {
      out = [];
      this.#order.forEach((_, entry) => out.push(entry));
    } else {
      out = this.#order.getInRange(range.start.dse * SEQ_SPAN, range.end.dse * SEQ_SPAN);
    }
    RowCellDataWork.multiCellsVisited += out.length;
    return out;
  }

  /** Give the ids fresh sequence numbers in their current order (2³¹ first writes into one row). */
  #renumber(): void {
    const slots = [...this.#byId.entries()].sort(([, a], [, b]) => a.seq - b.seq);
    this.#order.clear();
    this.#seq = 0;
    for (const [id, slot] of slots) {
      const fresh: MultiSlot = { entry: slot.entry, seq: ++this.#seq };
      this.#byId.set(id, fresh);
      this.#order.upsert(orderKey(fresh), fresh.entry);
    }
  }
}

/** A cell's place in the multi order: its first day, then its first-write order. */
function orderKey(slot: MultiSlot): number {
  return slot.entry.date.start.dse * SEQ_SPAN + slot.seq;
}

/** Same first and last day. */
function sameDays(a: NaiveDate.Partial, b: NaiveDate.Partial): boolean {
  return a.start.dse === b.start.dse && a.end.dse === b.end.dse;
}
